/**
 * FAQ Tab — 对照 ops.jsx FAQTable：单表、当前语言问句、点行开编辑。
 */

import type { Faq } from '~/data/queries';
import { useFaqCategories, useFaqs } from '~/data/queries';
import { langName } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { ContextMenu } from '~/ui/ContextMenu';
import { Section } from '~/ui/Section';
import { Table } from '~/ui/Table';
import { OpsSrcChip, deriveOpsSrc } from './OpsSrcChip';
import { type OpsMutations, errorToast } from './use-ops-mutations';

const FORBIDDEN_ROLE_TITLE = '权限不足 · 该操作受角色限制（后端 requireRole 为权威）';

export function FaqsTab({
  appId,
  lang,
  scope,
  canUpsert,
  mutations,
  onEdit,
  onManageCats,
  onConfirm,
}: {
  appId: string;
  lang: string;
  scope: string;
  canUpsert: boolean;
  mutations: OpsMutations;
  onEdit: (faq?: Faq) => void;
  onManageCats: () => void;
  onConfirm: (cfg: {
    title: string;
    message: string;
    tone?: 'danger' | 'warn' | 'default';
    confirmLabel?: string;
    onConfirm: () => void;
  }) => void;
}) {
  const { data: faqs } = useFaqs(appId, lang);
  const { data: faqCats } = useFaqCategories();
  const catLabelByKey = new Map(faqCats.map((c) => [c.key, c.label]));
  const catLabel = (key: string): string =>
    catLabelByKey.get(key) ?? faqs.find((f) => f.categoryKey === key)?.categoryLabel ?? key;

  const rows = [...faqs].sort((a, b) => a.sort - b.sort);

  const toggleActive = (f: Faq) => {
    mutations.updateFaq.mutate(
      { id: f.id, body: { is_active: !f.isActive } },
      { onError: (err) => errorToast(err, f.isActive ? '下架失败' : '上架失败') },
    );
  };

  return (
    <Section
      pad="4px 18px 16px"
      title={`FAQ · ${lang.toUpperCase()} ${langName(lang)}`}
      sub={
        scope === 'GLOBAL'
          ? '该语言全局 · 英语是源，其它语言从英语译出后可手改'
          : '该 App 该语言的覆盖 · 只有当前语言一项'
      }
      action={
        <div className="hstack" style={{ gap: 6 }}>
          <button
            type="button"
            className="btn btn-sm"
            title="分类是全部层一份注册表，不进 App 覆盖"
            onClick={onManageCats}
          >
            <Icons.IconFolder size={11} />
            管理分类
          </button>
          <button
            type="button"
            className="btn btn-sm"
            disabled={!canUpsert}
            title={canUpsert ? undefined : FORBIDDEN_ROLE_TITLE}
            onClick={() => canUpsert && onManageCats()}
          >
            <Icons.IconPlus size={11} />
            新建分类
          </button>
          <button
            type="button"
            className="btn btn-sm btn-accent"
            disabled={!canUpsert}
            title={canUpsert ? undefined : FORBIDDEN_ROLE_TITLE}
            onClick={() => canUpsert && onEdit()}
          >
            <Icons.IconPlus size={11} />
            新建问题
          </button>
        </div>
      }
    >
      <Table className="tbl">
        <thead>
          <tr>
            <th style={{ width: 54 }}>排序</th>
            <th>{lang.toUpperCase()} 问句</th>
            <th style={{ width: 130 }}>分类</th>
            <th style={{ width: 100 }}>翻译状态</th>
            <th style={{ width: 72 }}>上下架</th>
            <th style={{ width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((f) => {
            const q = f.questionI18n?.[lang]?.trim() ?? '';
            return (
              <tr
                key={f.id}
                style={{ cursor: canUpsert ? 'pointer' : 'default' }}
                onClick={() => canUpsert && onEdit(f)}
              >
                <td className="mono muted">{String(f.sort).padStart(2, '0')}</td>
                <td>
                  <div style={{ fontSize: 12.5, color: q ? 'var(--fg)' : 'var(--fg-faint)' }}>
                    {q || (
                      <span className="hstack" style={{ gap: 4 }}>
                        <Icons.IconAlert size={10} style={{ color: 'var(--warn)' }} />
                        该语言未译 · 接口不返回
                      </span>
                    )}
                  </div>
                  <div className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
                    {f.id} · language={lang}
                  </div>
                </td>
                <td>
                  <span className="chip" style={{ fontSize: 10 }}>
                    {catLabel(f.categoryKey)}
                  </span>
                </td>
                <td>
                  <OpsSrcChip src={deriveOpsSrc(lang, q)} />
                </td>
                <td>
                  <span
                    className={`chip ${f.status === '上架' ? 'chip-pos' : 'chip-warn'}`}
                    style={{ fontSize: 10 }}
                  >
                    {f.status}
                  </span>
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <ContextMenu
                    items={[
                      {
                        label: '编辑当前语言',
                        icon: 'IconLanguages',
                        disabled: !canUpsert,
                        title: canUpsert ? undefined : FORBIDDEN_ROLE_TITLE,
                        onClick: () => onEdit(f),
                      },
                      {
                        label: '复制',
                        icon: 'IconCopy',
                        disabled: true,
                        title:
                          '待契约（NOT-in-scope，见 docs/plans/2026-06-12-web-real-api-integration-plan.md §五）',
                      },
                      {
                        label: f.status === '上架' ? '下架' : '上架',
                        icon: f.status === '上架' ? 'IconPause' : 'IconPlay',
                        disabled: !canUpsert || mutations.updateFaq.isPending,
                        title: canUpsert ? undefined : FORBIDDEN_ROLE_TITLE,
                        onClick: () => toggleActive(f),
                      },
                      '---',
                      {
                        label: '删除',
                        icon: 'IconTrash',
                        tone: 'danger',
                        disabled: !canUpsert || f.isSeedBaseline,
                        title: !canUpsert
                          ? FORBIDDEN_ROLE_TITLE
                          : f.isSeedBaseline
                            ? '系统预置 FAQ 禁删，仅可下架'
                            : undefined,
                        onClick: () =>
                          onConfirm({
                            title: `删除 FAQ「${q || f.question}」？`,
                            message: '删除后客户端将不再展示该问答，不可恢复。',
                            tone: 'danger',
                            confirmLabel: '永久删除',
                            onConfirm: () =>
                              mutations.deleteFaq.mutate(f.id, {
                                onError: (err) => errorToast(err, '删除 FAQ 失败'),
                              }),
                          }),
                      },
                    ]}
                  >
                    <button
                      type="button"
                      className="btn btn-ghost btn-icon"
                      style={{ width: 22, height: 22 }}
                    >
                      <Icons.IconMoreV size={12} />
                    </button>
                  </ContextMenu>
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={6}
                style={{
                  padding: '26px 12px',
                  textAlign: 'center',
                  color: 'var(--fg-faint)',
                  fontSize: 11.5,
                }}
              >
                该语言暂无 FAQ 条目
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 10, lineHeight: 1.6 }}>
        点行编辑当前语言芯片这一门语言（每行只有该语言一个
        key，其他语言切芯片单独维护）。分类是全部层一份注册表，所有 App 与所有语言共用，不进 App
        覆盖。
      </div>
    </Section>
  );
}
